import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEnhancedAuth } from '../../contexts/EnhancedAuthContext';
import { determinePostAuthRoute } from '../../utils/paymentRouting';
import LoadingSpinner from './LoadingSpinner';

interface PaymentStatusGuardProps {
  children: React.ReactNode;
  requirePayment?: boolean;
}

const PaymentStatusGuard: React.FC<PaymentStatusGuardProps> = ({
  children,
  requirePayment = true
}) => {
  const { userDetails, userReady, checkPaymentStatus } = useEnhancedAuth();
  const navigate = useNavigate();

  const cachedDestination = requirePayment ? determinePostAuthRoute(userDetails?.agency) : null;
  const [isChecking, setIsChecking] = useState(!userDetails ? true : !!cachedDestination);
  const hasRecheckedRef = useRef(false);

  useEffect(() => {
    if (!requirePayment) {
      setIsChecking(false);
      return;
    }

    if (!userReady) {
      setIsChecking(true);
      return;
    }

    if (!userDetails || !userDetails.agency) {
      navigate('/LoginRegister');
      return;
    }

    if (!cachedDestination) {
      setIsChecking(false);
      return;
    }

    if (hasRecheckedRef.current) {
      return;
    }
    hasRecheckedRef.current = true;
    setIsChecking(true);

    checkPaymentStatus().then((freshAgency) => {
      const freshDestination = determinePostAuthRoute(freshAgency ?? userDetails.agency);
      if (freshDestination) {
        navigate(freshDestination);
      } else {
        setIsChecking(false);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userDetails, userReady, requirePayment, cachedDestination, navigate]);

  if (isChecking) {
    return <LoadingSpinner />;
  }

  return <>{children}</>;
};

export default PaymentStatusGuard;
